import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
} from "@mui/material";

import { ReactComponent as CloseIcon } from "assets/img/svg/CrossIcon.svg";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import AInput from "views/pages/manage/common/formFeilds/customInput/AInput";
import styles from "./processCard.module.scss";
import { fetchBuAndEmailsListByPid, sendEmail } from "./utils";
import ALoader from "views/pages/manage/common/a-loader";
import TablePagination from "modules/paymentManagement/cash-management/components/TablePagination";
import { successToast } from "views/pages/manage/common/utils/methods/toasterFunctions/function";

const RECIPIENT_TYPES = {
  DEFAULT: "DEFAULT",
  ADDITIONAL: "ADDITIONAL"
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendReportDialog = ({
  open,
  onClose,
  processId,
  processDetails,
  getCommissionReports,
  isViewOnly = false,
  viewData = {},
  businessAreas = []
}) => {

  const [recipientType, setRecipientType] = useState("");
  const [additionalEmails, setAdditionalEmails] = useState("");
  const [emailError, setEmailError] = useState("");
  const [recipientsByBU, setRecipientsByBU] = useState({});
  const [loading, setLoading] = useState(false);

  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const buDescriptionMap = useMemo(() => {
    return (businessAreas || []).reduce((acc, item) => {
      acc[item.label] = item.businessAreaDesc;
      return acc;
    }, {});
  }, [businessAreas]);

  const pagination = {
    total,
    page: pageNo,
    pageSize,
    totalPages,
  };
  const handleClose = () => {
    setAdditionalEmails("");
    setRecipientType("");
    setRecipientsByBU({});
    setEmailError("");
    setTotalPages(0);
    setPageSize(5);
    setPageNo(0);
    setTotal(0);
    onClose();
  }

  useEffect(() => {
    if (isViewOnly && viewData) {
      setRecipientType(viewData.recipientType || "");

      if (viewData.recipientType === RECIPIENT_TYPES.DEFAULT) {
        const mapped = (viewData.recipients || []).reduce((acc, item) => {
          const desc = buDescriptionMap[item.businessAreaName];

          const key = desc
            ? `${item.businessAreaName} ${desc || ""}`
            : item.businessAreaName;

          acc[key] = item.emails;
          return acc;
        }, {});
        setRecipientsByBU(mapped);
      }

      if (viewData.recipientType === RECIPIENT_TYPES.ADDITIONAL) {
        setAdditionalEmails(viewData.emails || "");
      }
    }
  }, [isViewOnly, viewData]);

  const fetchDefaultRecipients = async (processId, page = pageNo, size = pageSize) => {
    try {
      setLoading(true);
      const res = await fetchBuAndEmailsListByPid(processId, page, size);
      const data = res.content.reduce((acc, item) => {
        const desc = buDescriptionMap[item.businessAreaName];

        const key = desc
          ? `${item.businessAreaName} ${desc || ""}`
          : item.businessAreaName;

        acc[key] = item.emails;
        return acc;

      }, {});
      setRecipientsByBU(data);
      setTotal(res.totalElements || 0);
      setTotalPages(res.totalPages || 0);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const validateEmails = (value) => {
    if (!value.trim()) {
      setEmailError("");
      return true;
    }

    const emails = value.split(",").map((e) => e.trim());

    const invalidEmail = emails.find(
      (email) =>
        !EMAIL_REGEX.test(email) || !email.toLowerCase().endsWith("@aesl.in")
    );

    if (invalidEmail) {
      if (!EMAIL_REGEX.test(invalidEmail)) {
        setEmailError(`Invalid email: ${invalidEmail}`);
      } else {
        setEmailError(`Only @aesl.in emails are allowed: ${invalidEmail}`);
      }
      return false;
    }

    setEmailError("");
    return true;
  };

  const handleRecipientChange = useCallback((event, processId) => {
    if (isViewOnly) return;
    const value = event.target.value;
    setRecipientType(value);
    if (value === RECIPIENT_TYPES.DEFAULT) {
      setPageNo(0);
      fetchDefaultRecipients(processId, 0, pageSize);
    }
  }, [pageSize, isViewOnly]);

  const isSendDisabled = () => {
    if (recipientType === RECIPIENT_TYPES.DEFAULT) {
      return Object.keys(recipientsByBU).length === 0;
    }
    if (recipientType === RECIPIENT_TYPES.ADDITIONAL) {
      return additionalEmails.trim() === "" || emailError;
    }
    return true;
  };

  const handleSendReport = async () => {
    if (
      recipientType === RECIPIENT_TYPES.ADDITIONAL &&
      !validateEmails(additionalEmails)
    ) {
      return;
    }

    let emails = "";

    if (recipientType === RECIPIENT_TYPES.DEFAULT) {
      emails = Object.values(recipientsByBU).flat().join(",");
    } else if (recipientType === RECIPIENT_TYPES.ADDITIONAL) {
      emails = additionalEmails.split(",").map(e => e.trim()).join(",");
    }

    const raw = processDetails?.raw || {};

    const payload = {
      processId: processId,
      recipientType: recipientType,
      emails: emails,
      businessAreaType: raw.businessAreaType || "",
      subType: raw.subType || "",
      startDate: raw.startDate || "",
      endDate: raw.endDate || ""
    };

    try {
      setLoading(true);
      const res = await sendEmail(payload);
      if (res?.code == 200) {
        successToast(res?.message);
        getCommissionReports({});
        handleClose();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const nextButtonHandler = (page, size) => {
    setPageNo(page);
    fetchDefaultRecipients(processId, page, size);
  };

  const onPageSizeChangeHandler = (size) => {
    setPageSize(size);
    setPageNo(0);
    fetchDefaultRecipients(processId, 0, size);
  };

  return (
    <>
      {loading && <ALoader position={'fixed'} />}

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { maxWidth: "800px", width: "720px", borderRadius: "12px" } }}
      >

        <div className="d-flex align-items-center justify-content-between px-4 pt-3">
          <div className="large-text color-dark">
            Send Report via Email
          </div>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="px-4 pb-2 large-bold color-dark">
          Process ID: {processId}
        </div>

        <DialogContent>

          <div className="large-bold color-dark">
            Select Recipients
          </div>

          <RadioGroup
            value={recipientType}
            onChange={(e) => handleRecipientChange(e, processId)}
          >

            <div className={`${styles.recipientCard} semi-bolder color-dark`}>
              <FormControlLabel
                value={RECIPIENT_TYPES.DEFAULT}
                control={<Radio disabled={isViewOnly} />}
                label="Send to Default Recipients"
                sx={{
                  marginBottom: "0px",
                  '& .MuiFormControlLabel-label': { fontWeight: 700 }
                }}
              />
              <div className={styles.recipientDesc}>
                Send to all business area recipients
              </div>
            </div>

            <div className={`${styles.recipientCard} semi-bolder color-dark`}>
              <FormControlLabel
                value={RECIPIENT_TYPES.ADDITIONAL}
                control={<Radio disabled={isViewOnly} />}
                label="Send to Additional Recipients"
                sx={{
                  marginBottom: "0px",
                  '& .MuiFormControlLabel-label': { fontWeight: 700 }
                }}
              />
              <div className={styles.recipientDesc}>
                Add custom email addresses for this report
              </div>
            </div>

          </RadioGroup>

          {recipientType === RECIPIENT_TYPES.ADDITIONAL && (
            <div className="mt-3">
              <div className={`${styles.buTitle} large-bold color-dark`}>
                Additional Recipients
              </div>

              <AInput
                className={styles.emailInput}
                placeholder="Enter additional email addresses (comma separated)"
                value={additionalEmails}
                handler={(value) => {
                  if (isViewOnly) return;
                  setAdditionalEmails(value);
                  validateEmails(value);
                }}
                style={{ height: "45px" }}
                disabled={isViewOnly}
              />

              <div
                className={styles.helperText}
                style={{ color: emailError ? "#d32f2f" : "#6b7280" }}
              >
                {emailError
                  ? emailError
                  : "Enter custom email addresses separated by commas"}
              </div>
            </div>
          )}

          {recipientType === RECIPIENT_TYPES.DEFAULT && (
            <div className={`${styles.buScrollContainer} mt-3`}>
              {Object.entries(recipientsByBU).map(([bu, emails]) => (
                <div key={bu} className={styles.buCard}>
                  <div className={styles.buTitle}>{bu} </div>
                  <div className={styles.emailChipContainer}>
                    {emails.map((email, index) => (
                      <div key={index} className={styles.emailChip}>
                        {email}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isViewOnly && recipientType === RECIPIENT_TYPES.DEFAULT && totalPages > 0 && (
            <TablePagination
              loading={loading}
              pagination={pagination}
              onPageChange={nextButtonHandler}
              onPageSizeChange={onPageSizeChangeHandler}
            />
          )}

          {!isViewOnly && (
            <div className="d-flex justify-content-end mt-4">
              <AButton
                variant="outline_danger"
                onClick={handleClose}
                className="mr-2"
              >
                Cancel
              </AButton>

              <AButton
                variant="primary_filled"
                onClick={handleSendReport}
                disabled={isSendDisabled()}
              >
                Send Report
              </AButton>
            </div>
          )}

        </DialogContent>

      </Dialog>
    </>
  );
};

export default SendReportDialog;